import React, { JSX, useEffect, useState } from "react";
import { Paragraph, ParagraphSize } from "../../typography/paragraph/paragraph";
import { ReportsBaseProps } from "components/reports/reports-base-props";
import { SkipNavContent } from "@chakra-ui/skip-nav";
import { t } from "utilities/localization/t";
import { useReportReducer } from "utilities/hooks/use-report-reducer";
import { initialState, initializer, reducer, validate } from "./report-aen-provider-invoice-reducer";
import ReportProviderSelection from "../components/report-provider-selection/report-provider-selection";
import ReportSection, { ReportSectionSizes } from "../components/report-section/report-section";
import { FormCalendarDateRangePicker } from "components/form/form-calendar-date-range-picker/form-calendar-date-range-picker";
import { FormRadioCardList, RadioCard, RadioCardStyle, RadioCardGridColumns } from "components/form/form-radio-card-list/form-radio-card-list";
import { IconSizes } from "components/icons/constants/icon-sizes";
import { PROVIDER_STATUS_DESCRIPTIONS, PROVIDER_STATUS_VALUES } from "../components/report-provider-status/report-provider-status";
import { Icons } from "components/icons/constants/icons";
import "./report-aen-provider-invoice.scss";

// -------------------------------------------------------------------------------------------------
// #region Interfaces
// -------------------------------------------------------------------------------------------------

interface ReportAenProviderInvoiceProps extends ReportsBaseProps {}

// #endregion Interfaces

// -------------------------------------------------------------------------------------------------
// #region Constants
// -------------------------------------------------------------------------------------------------

const CSS_CLASS_NAME = "report-aen-provider-invoice";

// #endregion Constants

// -------------------------------------------------------------------------------------------------
// #region Component
// -------------------------------------------------------------------------------------------------

const ReportAenProviderInvoice: React.FC<ReportAenProviderInvoiceProps> = ({
    onChange: onChangeReportCriteria,
    
}): JSX.Element => {
    const [payload, dispatch] = useReportReducer(reducer, initialState, {
        initializer: initializer(),
        validate,
    });

    const {  
        isValid, 
        aenProviders, 
        dateRangeStart,
        dateRangeEnd, 
        aenProviderStatusActive } = payload;

    const handleDateRangeChange = (date?: Array<Date | null>) => {
        const [start, end] = date ?? [];
        dispatch({ type: "dateRange", dateRangeStart: start, dateRangeEnd: end });
    };
    
    useEffect(() => {
        const aenProviderIds = aenProviders.map((id) => id.id ?? 0);
        const aenProviderStatusIsActive = aenProviderStatusActive === PROVIDER_STATUS_VALUES.ACTIVE ? true : aenProviderStatusActive === PROVIDER_STATUS_VALUES.INACTIVE ? false : undefined;
        
        onChangeReportCriteria({
            isValid: isValid,
            AENProviderIds: aenProviderIds,
            dateRangeStart: dateRangeStart,
            dateRangeEnd: dateRangeEnd,
            providerStatusActive: aenProviderStatusIsActive
        });
    }, [isValid, onChangeReportCriteria, aenProviders, dateRangeStart, dateRangeEnd, aenProviderStatusActive]);


    const PROVIDER_STATUS_CARDS = Object.values(PROVIDER_STATUS_VALUES).map(
    (key): RadioCard => ({
        icon: Icons.Reporting,
        text: PROVIDER_STATUS_DESCRIPTIONS[key],
        value: key,
    }));

    const handleProviderStatusChange = (value: string) => {   
        setTranslatedProviderStatus(value);     
        dispatch({ type: "aenProviderStatusActive", aenProviderStatusActive: value });
    };
    
    const [translatedProviderStatus, setTranslatedProviderStatus] = useState<string>(PROVIDER_STATUS_VALUES.ACTIVE_AND_INACTIVE);

    return (
        <>
            <div className="content-wrap">
                <div className="content">
                    <SkipNavContent>
                        <div className={CSS_CLASS_NAME}>
                            <h2>{t("aenInvoiceReport")}</h2>
                            <ReportSection>
                                <div className="report-section">
                                    <ReportProviderSelection
                                        providers={aenProviders}
                                        onProvidersChanged={(aenProviders) =>
                                            dispatch({ type: "updateProviders", aenProviders })
                                        }
                                    />
                                </div>
                            </ReportSection>
                            <ReportSection>
                                <Paragraph size={ParagraphSize.Large}>
                                    {t("aenInvoiceReport_forProviderStatus")}
                                </Paragraph>
                                <FormRadioCardList
                                    id="aenProviderStatus"
                                    formFieldName="aenProviderStatus"
                                    label={t("invoiceStatusType")}
                                    labelScreenReaderOnly={true}
                                    iconSize={IconSizes.Base}
                                    value={translatedProviderStatus}
                                    radioCards={PROVIDER_STATUS_CARDS}
                                    required={true}
                                    style={RadioCardStyle.Horizontal}
                                    onChange={handleProviderStatusChange}
                                    columns={RadioCardGridColumns.Three}
                                />
                            </ReportSection>
                        
                            <ReportSection size={ReportSectionSizes.Half}>
                                    <Paragraph size={ParagraphSize.Large}>
                                        {t("duringThisTimePeriod")}
                                    </Paragraph>
                                    <FormCalendarDateRangePicker
                                        endDate={dateRangeEnd}
                                        formFieldName="dateRange"
                                        handleEventDateRangeChange={handleDateRangeChange}
                                        label={t("dateRange")}
                                        startDate={dateRangeStart}
                                    />
                            </ReportSection>
                            </div>
                    </SkipNavContent>
                </div>
            </div>
        </>
    );
};

// #endregion Component

// -------------------------------------------------------------------------------------------------
// #region Exports
// -------------------------------------------------------------------------------------------------

export { ReportAenProviderInvoice };

// #endregion Exports
